/**
 * Validation System - PSP/CRE Tool v2.0
 * Cross-tab validation and scenario integrity checks
 * 
 * VERSION: 1.0.1 (February 13, 2026)
 * 
 * CHANGELOG:
 * v1.0.1 (Feb 13, 2026): ESLint compliance fix
 * - FIXED: ESLint error "import/no-anonymous-default-export"
 * - CHANGED: Line 350-357 - Assign object to variable before export
 * - RESULT: Zero ESLint warnings, production-ready
 * 
 * v1.0.0 (Jan 29, 2026): Initial release
 * - PHASE 4.7.3: Resupply Adequacy Validation
 * - Validates blood supply adequacy before scenario save
 * - Prevents deployment with critical shortages
 * - Provides actionable fix recommendations
 * - Integrates with App.jsx save handler
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 13, 2026
 */

// ============================================================================
// RESUPPLY ADEQUACY VALIDATION
// ============================================================================

/**
 * Validate resupply adequacy before scenario save
 * 
 * Checks:
 * - Has calculator been run?
 * - Is there a stockout during operation?
 * - Is final DOS critically low?
 * - Is minimum DOS below NATO standards?
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {Object} - { hasErrors, hasWarnings, errors: [], warnings: [] }
 */
export function validateResupplyAdequacy(scenario) {
  const errors = [];
  const warnings = [];
  
  // -------------------------------------------------------------------------
  // Check 1: Has calculator been run?
  // -------------------------------------------------------------------------
  const medReq = scenario?.medical_requirements;
  if (!medReq || !medReq.resupply_summary) {
    warnings.push({
      type: 'NO_CALCULATION',
      message: 'Calculator has not been run yet',
      fix: 'Go to Tab 5 and run the calculator before saving',
      severity: 'WARNING',
      tab: 4 // Tab 5 (0-indexed)
    });
    
    // If no calculation, we can't check further - return early
    return { 
      hasErrors: false, 
      hasWarnings: true, 
      errors, 
      warnings 
    };
  }
  
  // -------------------------------------------------------------------------
  // Check 2: Stockout during operation?
  // -------------------------------------------------------------------------
  const resupplySummary = medReq.resupply_summary;
  const stockoutDay = resupplySummary?.dos_projection?.stockout_day;
  const duration = scenario.operation_parameters?.duration_days || 0;
  
  if (stockoutDay && stockoutDay <= duration) {
    errors.push({
      type: 'STOCKOUT',
      message: `CRITICAL: Blood supply stockout on Day ${stockoutDay} of ${duration}`,
      fix: 'Use Auto-Schedule in Tab 4 or manually add resupply events before stockout day',
      severity: 'CRITICAL',
      tab: 3, // Tab 4 (0-indexed)
      data: { stockout_day: stockoutDay, duration }
    });
  }
  
  // -------------------------------------------------------------------------
  // Check 3: Critically low final DOS?
  // -------------------------------------------------------------------------
  const finalDOS = resupplySummary?.dos_projection?.final_dos;
  if (finalDOS !== null && finalDOS !== undefined) {
    if (finalDOS < 1) {
      errors.push({
        type: 'CRITICAL_LOW_DOS',
        message: `CRITICAL: Final DOS dangerously low (${finalDOS.toFixed(1)} days)`,
        fix: 'Add resupply events to achieve at least 3 days final DOS',
        severity: 'CRITICAL',
        tab: 3,
        data: { final_dos: finalDOS, target_dos: 3 }
      });
    } else if (finalDOS < 3) {
      warnings.push({
        type: 'LOW_DOS',
        message: `Final DOS below recommended minimum (${finalDOS.toFixed(1)} days)`,
        fix: 'Consider adding resupply to achieve 5+ days DOS (NATO standard)',
        severity: 'WARNING',
        tab: 3,
        data: { final_dos: finalDOS, target_dos: 5 }
      });
    } else if (finalDOS < 5) {
      warnings.push({
        type: 'SUBOPTIMAL_DOS',
        message: `Final DOS below NATO standard (${finalDOS.toFixed(1)} days)`,
        fix: 'NATO recommends 5+ days final DOS for operational flexibility',
        severity: 'INFO',
        tab: 3,
        data: { final_dos: finalDOS, target_dos: 5 }
      });
    }
  }
  
  // -------------------------------------------------------------------------
  // Check 4: Minimum DOS during operation?
  // -------------------------------------------------------------------------
  const minimumDOS = resupplySummary?.dos_projection?.minimum_dos;
  if (minimumDOS !== null && minimumDOS !== undefined && minimumDOS < 3 && !stockoutDay) {
    warnings.push({
      type: 'LOW_MINIMUM_DOS',
      message: `Minimum DOS drops to ${minimumDOS.toFixed(1)} days during operation`,
      fix: 'Add resupply events earlier in the operation to maintain 3+ days minimum',
      severity: 'WARNING',
      tab: 3,
      data: { minimum_dos: minimumDOS, target_dos: 3 }
    });
  }
  
  // -------------------------------------------------------------------------
  // Check 5: Monte Carlo high failure probability?
  // -------------------------------------------------------------------------
  const monteCarlo = resupplySummary?.dos_projection?.monte_carlo;
  if (monteCarlo?.enabled && monteCarlo.stockout_probability > 0.2) {
    warnings.push({
      type: 'HIGH_STOCKOUT_RISK',
      message: `${Math.round(monteCarlo.stockout_probability * 100)}% probability of stockout (route modeling)`,
      fix: 'Consider using more reliable delivery methods or adding contingency resupply',
      severity: 'WARNING',
      tab: 3,
      data: { 
        stockout_probability: monteCarlo.stockout_probability,
        average_stockout_day: monteCarlo.average_stockout_day 
      }
    });
  }
  
  return {
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0,
    errors,
    warnings
  };
}

// ============================================================================
// FORCE COMPOSITION VALIDATION
// ============================================================================

/**
 * Validate force composition completeness
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {Object} - Validation results
 */
export function validateForceComposition(scenario) {
  const errors = [];
  const warnings = [];
  
  // Check if force builder has results
  const forceBuilder = scenario?.force?.forceBuilderResult;
  if (!forceBuilder || !forceBuilder.brigades || forceBuilder.brigades.length === 0) {
    errors.push({
      type: 'NO_FORCE_STRUCTURE',
      message: 'Force structure not configured',
      fix: 'Use Force Builder in Tab 1 to create force structure',
      severity: 'CRITICAL',
      tab: 0
    });
  }
  
  return {
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0,
    errors,
    warnings
  };
}

// ============================================================================
// MEDICAL SYSTEM VALIDATION
// ============================================================================

/**
 * Validate medical system configuration
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {Object} - Validation results
 */
export function validateMedicalSystem(scenario) {
  const errors = [];
  const warnings = [];
  
  const medConfig = scenario?.medical_configuration;
  
  // Check if MTFs configured
  if (!medConfig?.mtf_configuration || !medConfig.mtf_configuration.role1) {
    warnings.push({
      type: 'NO_MTF_CONFIG',
      message: 'Medical treatment facilities not configured',
      fix: 'Configure MTFs in Tab 3 Section 3A',
      severity: 'WARNING',
      tab: 2
    });
  }
  
  // Check if evacuation assets configured
  if (!medConfig?.evacuation_assets || medConfig.evacuation_assets.ambulances === 0) {
    warnings.push({
      type: 'NO_EVAC_ASSETS',
      message: 'No evacuation assets configured',
      fix: 'Configure ambulances and MEDEVAC in Tab 3 Section 3B',
      severity: 'WARNING',
      tab: 2
    });
  }
  
  return {
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0,
    errors,
    warnings
  };
}

// ============================================================================
// COMPREHENSIVE SCENARIO VALIDATION
// ============================================================================

/**
 * Validate entire scenario before save
 * Runs all validation checks and aggregates results
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {Object} - Aggregated validation results
 */
export function validateScenario(scenario) {
  const results = {
    force: validateForceComposition(scenario),
    medical: validateMedicalSystem(scenario),
    resupply: validateResupplyAdequacy(scenario)
  };
  
  // Aggregate all errors and warnings
  const allErrors = [
    ...results.force.errors,
    ...results.medical.errors,
    ...results.resupply.errors
  ];
  
  const allWarnings = [
    ...results.force.warnings,
    ...results.medical.warnings,
    ...results.resupply.warnings
  ];
  
  // Categorize by severity
  const critical = allErrors.filter(e => e.severity === 'CRITICAL');
  const errors = allErrors.filter(e => e.severity === 'ERROR');
  const warnings = allWarnings.filter(w => w.severity === 'WARNING');
  const info = allWarnings.filter(w => w.severity === 'INFO');
  
  return {
    hasErrors: allErrors.length > 0,
    hasWarnings: allWarnings.length > 0,
    hasCritical: critical.length > 0,
    
    // Categorized results
    critical,
    errors,
    warnings,
    info,
    
    // All results
    allErrors,
    allWarnings,
    
    // Per-section results
    bySection: results,
    
    // Summary
    summary: {
      total_issues: allErrors.length + allWarnings.length,
      critical_count: critical.length,
      error_count: errors.length,
      warning_count: warnings.length,
      info_count: info.length,
      can_save: allErrors.length === 0
    }
  };
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Get validation severity color
 * 
 * @param {string} severity - CRITICAL, ERROR, WARNING, INFO
 * @returns {string} - Material-UI color name
 */
export function getValidationColor(severity) {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
      return 'info';
    default:
      return 'default';
  }
}

/**
 * Get validation severity icon name
 * 
 * @param {string} severity - CRITICAL, ERROR, WARNING, INFO
 * @returns {string} - Icon name for Material-UI
 */
export function getValidationIcon(severity) {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
      return 'info';
    default:
      return 'help';
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// ESLint fix (Feb 13, 2026): Assign to variable before exporting
// Rule: import/no-anonymous-default-export
const ValidationService = {
  validateResupplyAdequacy,
  validateForceComposition,
  validateMedicalSystem,
  validateScenario,
  getValidationColor,
  getValidationIcon
};

export default ValidationService;
